import React, { useState } from 'react';
import {
    Box,
    Button,
    Drawer,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack, Chip, Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { ReportStatus } from '../../../utils/graphql/generatedTS/graphql';
import { FilterAltOutlined } from '@mui/icons-material';

interface FilterValues {
    campaignName?: string;
    startDate?: number;
    endDate?: number;
    status?: ReportStatus;
}

interface FilterDropdownProps {
    onApplyFilters: (filters: FilterValues) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ onApplyFilters }) => {
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [campaignName, setCampaignName] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [status, setStatus] = useState<ReportStatus | ''>('');

    const handleApply = () => {
        onApplyFilters({
            campaignName: campaignName || undefined,
            startDate: startDate ? new Date(startDate).getTime()/1000 : undefined,
            endDate: endDate ? new Date(endDate).getTime()/1000 : undefined,
            status: status || undefined,
        });
        setDrawerOpen(false);
    };

    const handleClear = () => {
        setCampaignName('');
        setStartDate(null);
        setEndDate(null);
        setStatus('');
        onApplyFilters({
            campaignName: undefined,
            startDate: undefined,
            endDate: undefined,
            status: undefined,
        });
        setDrawerOpen(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Button
                variant="contained"
                color="secondary"
                onClick={() => setDrawerOpen(true)}
                startIcon={<FilterAltOutlined />}
                style={{ fontWeight: 'bold', fontSize: '14px' }}
            >
                Show filter
            </Button>
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={() => setDrawerOpen(false)}
            >
                <Box p={3} width={280}>
                    <Stack spacing={4}>
                        <Typography variant={"h3"} fontWeight={600}>Filters</Typography>
                        <Typography variant={"subtitle2"} style={{marginTop:8}}>Fill in at least one of the fields below to filter the Reports Table</Typography>
                        <TextField
                            fullWidth
                            label="Campaign Name"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '10px' } },
                                '& .MuiInputLabel-root': { color: 'gray' }
                            }}
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                        />
                            <DatePicker
                                label="Start Date"
                                format="dd/MM/yyyy"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: { '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '10px' } },
                                            '& .MuiInputLabel-root': { color: 'gray' } },
                                    },
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                format="dd/MM/yyyy"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                minDate={startDate}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: { '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '10px' } },
                                            '& .MuiInputLabel-root': { color: 'gray' } },
                                    },
                                }}
                            />
                        <FormControl size="small" >
                            <InputLabel sx={{ color: 'gray'}}>Status</InputLabel>
                            <Select
                                value={status}
                                size="small"
                                sx={{borderRadius: '10px', '& .MuiInputLabel-root': { color: 'gray' }}}
                                label="Status"
                                onChange={(e) => setStatus(e.target.value as '' | ReportStatus)}
                            >
                                <MenuItem value={ReportStatus.Succeeded}><Chip size="small" label="Done" color="success" sx={{ fontSize: 12 }} /></MenuItem>
                                <MenuItem value={ReportStatus.PostprocessFailed}><Chip size="small" label="Postprocess Failed" color="error" sx={{ fontSize: 12 }} /></MenuItem>
                                <MenuItem value={ReportStatus.EngineFailed}><Chip size="small" label="Failed" color="error" sx={{ fontSize: 12 }} /></MenuItem>
                            </Select>
                        </FormControl>
                        <Box display="flex" gap={3}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleApply}
                                style={{
                                    backgroundImage: 'linear-gradient(to right, #8A5FFD, #5EE0E6)',
                                    color: 'white',
                                }}
                            >
                                Apply
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleClear}
                                style={{ borderRadius: 20 }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Drawer>
        </LocalizationProvider>
    );
};

export default FilterDropdown;
